import OrganizationTypeRepository from '@api/repositories/organization-type-repository'
import { IOrganizationType } from '@app/interfaces/organization/organization-type'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import {
  createOrganizationType,
  updateOrganizationType,
} from '@app/store/actions/organization-types-actions'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from 'antd'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

interface props {
  organizationType?: IOrganizationType
  edit?: boolean
}

const OrganizationTypeForm: React.FC<props> = ({ organizationType, edit }) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()
  const educationGrades = ['GOVERNMENT_AGENCY', 'INSTITUTION', 'DEFAULT']
  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IOrganizationType = {
      id: values.id,
      sorting: values.sorting,
      status: values.status,
      data: languages,
      type: values.type,
    }
    setConfirmLoading(true)
    const employeeEducationSubmitApi = organizationType
      ? OrganizationTypeRepository.update
      : OrganizationTypeRepository.create

    await employeeEducationSubmitApi(newValues)
      .then((response: AxiosResponse<IOrganizationType>) => {
        if (organizationType && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateOrganizationType(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!organizationType && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createOrganizationType(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={edit ? t('organizationType.edit') : t('organizationType.add')}
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: organizationType?.id,
              sorting: organizationType?.sorting || 0,
              status: edit ? organizationType?.status : true,
              type: organizationType?.type,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={organizationType?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label={t('organizationType.types.type')} name={'type'}>
              <Select disabled={confirmLoading}>
                {educationGrades.map((institutionStatusTypes, index) => {
                  return (
                    <Select.Option key={index} value={institutionStatusTypes}>
                      {t(`organizationType.types.${institutionStatusTypes}`)}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
            <FormList
              confirmLoading={confirmLoading}
              object={organizationType}
            />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default OrganizationTypeForm
